import { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useSearchParams } from 'react-router-dom';

import { captureException, setContext } from '@sentry/react';
import { getFunctions, httpsCallable } from 'firebase/functions';

import styles from './Complete.module.scss';

import {
  defaultError,
  invalidParametersError,
  inviteError,
  oauthError,
  signInError
} from '../../config/errors.config';
import { APP_TITLE } from '../../constants/app';
import { SENTRY_SERVICE_CONTEXT } from '../../constants/sentry';
import {
  sessionAuthTokenKey,
  sessionEmailsSentKey,
  sessionHostKey,
  sessionIdeKey,
  sessionMachineKey,
  sessionOrgKey,
  sessionRepoKey,
  sessionRequestCodeVerifierKey,
  sessionRequestStateKey,
  sessionSchemeKey,
  sessionServiceKey,
  sessionUserTokenKey,
  sessionUsernameKey,
  sessionValidationKey
} from '../../constants/storage';
import { app } from '../../firebase';
import { getService } from '../../services';
import { ServiceError } from '../../services/errors';
import { Service } from '../../services/service';
import { CompleteCard } from '../../stories/CompleteCard/CompleteCard';
import { EmailSentCard } from '../../stories/EmailSentCard/EmailSentCard';
import { ErrorCard } from '../../stories/ErrorCard/ErrorCard';
import { InviteTeamMemberParams } from '../../types/email.types';
import {
  EmailCodeVerificationResponse,
  OAuthResponse,
  ValidationResponse
} from '../../types/model.types';
import {
  Organization,
  Owner,
  Repository,
  Teammate,
  User
} from '../../types/services.types';
import { getUrlParams } from '../../util/params';
import { sanitize } from '../../util/sanitize';
import { get, remove, set } from '../../util/sessionStorage';

interface CompleteParams {
  code?: string;
  email_code?: string;
  error?: string;
  host?: string;
  ide?: string;
  machine?: string;
  org?: string;
  scheme?: string;
  service?: string;
  state?: string;
  token?: boolean;
}

const parseWebCallback = (value: string): URL | undefined => {
  try {
    // the vscode extension sends the callback url base64url-encoded: a percent-encoded url
    // does not survive the encode/decode layers between the extension host and this page
    // (vscode's openExternal re-serialization merges the callback's own &-separated params
    // into the signin url's), while base64url passes through them all untouched. Plain urls
    // are still accepted as a fallback
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = /^[A-Za-z0-9+/]+$/.test(base64)
      ? atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4))
      : value;

    const url = new URL(decoded);

    return url.protocol === 'http:' || url.protocol === 'https:'
      ? url
      : undefined;
  } catch {
    return undefined;
  }
};

export default function Complete() {
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [service, setService] = useState<Service>();
  const [inviteToken, setInviteToken] = useState<string>();
  const [user, setUser] = useState<User>();
  const [organization, setOrganization] = useState<Organization>();
  const [repository, setRepository] = useState<Repository>();
  const [teammates, setTeammates] = useState<Teammate[]>([]);
  const [org, setOrg] = useState<string>();
  const [repo, setRepo] = useState<string>();
  const [ide, setIde] = useState<string>();
  const [scheme, setScheme] = useState<string>();
  const [userMachine, setUserMachine] = useState<string>();
  const [token, setToken] = useState<string>();
  const [deepLink, setDeepLink] = useState<string>();
  const [emailSent, setEmailSent] = useState<string>();
  const [isOauthSignin, setIsOauthSignin] = useState<boolean>(true);
  const [error, setError] = useState<ServiceError>();

  useEffect(() => {
    setupView();
  }, []);

  const setupView = async () => {
    try {
      const {
        code,
        state,
        error,
        token,
        service,
        host,
        scheme,
        org,
        ide,
        machine,
        email_code
      } = getUrlParams<CompleteParams>(searchParams);

      setContext(SENTRY_SERVICE_CONTEXT, {
        token,
        email: !!email_code
      });

      setIsOauthSignin(!token && !email_code);

      if (error) {
        throw new ServiceError(signInError.title, error || signInError.message);
      }

      const sessionService = service ?? get(sessionServiceKey);
      const sessionHost = host ?? get(sessionHostKey);
      const sessionOrg = org ?? get(sessionOrgKey);
      const sessionRepo = get(sessionRepoKey);
      const sessionIde = ide ?? get(sessionIdeKey);
      const sessionScheme = scheme ?? get(sessionSchemeKey);
      const sessionState = get(sessionRequestStateKey);
      const sessionUserToken = get(sessionUserTokenKey);
      const sessionUsername = get(sessionUsernameKey);
      const sessionMachine = get(sessionMachineKey);
      let sessionCodeVerifier = get(sessionRequestCodeVerifierKey);

      if (
        !sessionService ||
        !sessionHost ||
        !sessionOrg ||
        !sessionIde ||
        !sessionScheme
      ) {
        throw new ServiceError(
          invalidParametersError.title,
          invalidParametersError.message
        );
      }

      const codeService = getService(
        sessionService,
        sessionHost,
        Boolean(token),
        sessionUsername
      );
      setService(codeService);
      setOrg(sessionOrg);
      sessionRepo && setRepo(sessionRepo);
      setUserMachine(machine);
      setIde(sessionIde);
      setScheme(sessionScheme);

      let oauthResponse: OAuthResponse | undefined;
      if (email_code) {
        const emailCodeVerification = await verifyEmailCode(email_code);
        oauthResponse = {
          oauthToken: emailCodeVerification.token,
          createdAt: Math.round(new Date().getTime() / 1000)
        };
        sessionCodeVerifier = emailCodeVerification.codeVerifier;
        setToken(emailCodeVerification.token);
        setInviteToken(emailCodeVerification.inviteToken);
      } else if (token && sessionUserToken) {
        oauthResponse = {
          oauthToken: sessionUserToken,
          createdAt: Math.round(new Date().getTime() / 1000)
        };
        setToken(sessionUserToken);
      } else if (code && state === sessionState) {
        oauthResponse = await validateToken(code);
        setToken(oauthResponse.oauthToken);
      }

      if (!oauthResponse)
        throw new ServiceError(oauthError.title, oauthError.message);

      const [hasAccessToRepoProvided, user, currentUserEmails, organization] =
        await Promise.all([
          // Check if the user has access to the repo provided, if no repo will ust be true
          codeService.hasAccessToRepoProvided(
            oauthResponse.oauthToken,
            sessionOrg,
            sessionRepo
          ),
          // Get the current user
          codeService.getUser(oauthResponse.oauthToken, sessionOrg),
          // Check if we have the email permissions
          codeService.getUserEmails(oauthResponse.oauthToken, sessionOrg),
          // Check if we have access to orgs
          codeService.getOrganization(oauthResponse.oauthToken, sessionOrg)
        ]);

      if (!hasAccessToRepoProvided) {
        throw new ServiceError(
          `No access to ${sessionOrg}/${sessionRepo}`,
          `${token ? `Your ${codeService.patName.toLowerCase()}` : `${user.displayName} (${user.login})`} does not have access to ${sessionOrg}/${sessionRepo} on ${sessionHost}`
        );
      }

      // If they signed in with a repo (repo param present) then only get users from that repo
      // Else they signed in with an org and we can get users from the the 5 most recent repos
      const reposForTeammates = sessionRepo
        ? [sessionRepo]
        : await codeService.getMostRecentRepos(
            oauthResponse.oauthToken,
            sessionOrg
          );

      // Get the last 100 commits from the last 5 active repos
      const commits = await codeService.getLatestCommitsFromRepos(
        oauthResponse.oauthToken,
        sessionOrg,
        reposForTeammates
      );

      // Get the invite token and members here, skip if we have come from the confirmation email
      let orgMembers: string[] = [];
      if (!email_code) {
        const owner: Owner = {
          id: organization.id,
          name: sessionOrg,
          type: organization.type,
          createdAt: organization.createdAt,
          enterprise: organization.enterprise
        };
        const { sentSignInEmail, inviteToken, members, emailAddress } =
          await getInviteTokenAndMembers(
            oauthResponse.oauthToken,
            sessionService,
            sessionHost,
            sessionUsername,
            user.displayName,
            user.email || currentUserEmails[0],
            user.id,
            owner,
            sessionRepo,
            sessionScheme,
            sessionIde,
            sessionMachine,
            token
          );

        // Throw error if a confirmation email was sent
        if (sentSignInEmail) {
          setEmailSent(emailAddress);

          return;
        }

        setInviteToken(inviteToken);
        orgMembers = members || [];
      }

      // Create the repository for the email template, if there is no repo just use org name
      if (sessionRepo) {
        const repository: Repository = {
          displayName: sessionRepo
            ? `${sessionOrg}/${sessionRepo.replace('/_git/', '/')}`
            : sessionRepo,
          orgOrRepo: sessionRepo ? codeService.repoText : codeService.orgText,
          photoURL: organization.photoURL
        };

        setRepository(repository);
      }

      // Get emails sent already
      const sentEmailsString = get(sessionEmailsSentKey);
      const sentEmails: string[] = sentEmailsString
        ? JSON.parse(sentEmailsString)
        : [];

      const teammates = commits
        .flatMap((it) => it)
        .filter((it) => !currentUserEmails.includes(it.email))
        .filter(
          (it) =>
            !codeService.emailsToIgnore.some((email) =>
              it.email.endsWith(email)
            )
        )
        .map((it) => {
          const sanitizedEmail = sanitize(it.email);

          return {
            ...it,
            id: sanitizedEmail,
            sent: sentEmails.includes(sanitizedEmail),
            placeholder: it.photoURL,
            signedUp: orgMembers.includes(sanitizedEmail),
            photoURL: it.photoURL
          };
        })
        .reduce(
          (acc, teammate) => ({ ...acc, [teammate.email]: teammate }),
          {} as { [key: string]: Teammate }
        );

      setDeepLink(
        generateDeepLink(
          sessionHost,
          sessionOrg,
          sessionService,
          sessionScheme,
          sessionRepo,
          sessionCodeVerifier,
          sessionUserToken,
          sessionUsername,
          oauthResponse.oauthToken,
          oauthResponse.createdAt,
          oauthResponse.refreshToken,
          oauthResponse.expiresIn,
          email_code
        )
      );

      setUser(user);
      setOrganization(organization);
      setIde(sessionIde);
      setScheme(sessionScheme);

      // TODO: This can be moved to a function

      setTeammates(
        Object.values(teammates).map((it) => ({
          ...it,
          sent: sentEmails.includes(it.id)
        }))
      );
    } catch (err: any) {
      console.log(err);
      captureException(err);

      if (err instanceof ServiceError) {
        setError(err);
      } else {
        setError(
          new ServiceError(
            defaultError.title,
            err.message || defaultError.message
          )
        );
      }
    }

    setLoading(false);
  };

  const verifyEmailCode = async (
    emailCode: string
  ): Promise<EmailCodeVerificationResponse> => {
    try {
      const functions = getFunctions(app);
      const verifyEmailCode = httpsCallable(functions, 'verifyEmailCode');
      const response = await verifyEmailCode({ emailCode });

      return response.data as EmailCodeVerificationResponse;
    } catch (error: any) {
      throw new ServiceError(signInError.title, signInError.message);
    }
  };

  const performFinalOAuthStep = async (
    code: string
  ): Promise<OAuthResponse> => {
    let data: OAuthResponse;

    const storageAuthToken = get(sessionAuthTokenKey);
    if (storageAuthToken) {
      data = JSON.parse(storageAuthToken);
    } else {
      const functions = getFunctions(app);
      const oauthSecondLeg = httpsCallable(functions, 'oauthSecondLeg');
      const response = await oauthSecondLeg({
        code,
        service: get(sessionServiceKey),
        orgId: get(sessionOrgKey),
        codeVerifier: get(sessionRequestCodeVerifierKey)
      });
      data = response.data as OAuthResponse;

      if (data.error)
        throw new ServiceError(
          signInError.title,
          data.error || signInError.message
        );
    }

    return data;
  };

  const getInviteTokenAndMembers = async (
    token: string,
    service: string,
    host: string,
    username: string | null,
    name: string,
    email: string,
    userId: string,
    owner: Owner,
    repo: string | null,
    scheme: string,
    ide: string,
    machine: string | null,
    usingToken?: boolean
  ): Promise<ValidationResponse> => {
    let data: ValidationResponse;
    const validationKey = get(sessionValidationKey);
    if (validationKey) {
      data = JSON.parse(validationKey);
    } else {
      const functions = getFunctions(app);
      const getInviteToken = httpsCallable(functions, 'getInviteToken');
      const response = await getInviteToken({
        token,
        service,
        host,
        username,
        name,
        email,
        userId,
        owner,
        repo,
        scheme,
        ide,
        machine,
        usingToken
      });
      data = response.data as ValidationResponse;

      if (data.error) throw new ServiceError('Failed to sign in', data.error);

      // Save response in session storage in case of user refreshing
      set(sessionValidationKey, JSON.stringify(response.data));
    }

    return data;
  };

  const validateToken = async (code: string): Promise<OAuthResponse> => {
    try {
      const response = await performFinalOAuthStep(code);

      // Save token in session storage in case of user refreshing
      set(sessionAuthTokenKey, JSON.stringify(response));

      return response;
    } catch (error: any) {
      // Clear token if we get an error
      remove(sessionAuthTokenKey);
      throw new ServiceError(
        signInError.title,
        error.message || signInError.message
      );
    }
  };

  const generateDeepLink = (
    host: string,
    org: string,
    service: string,
    scheme: string,
    repo: string | null,
    codeVerifier: string | null,
    userToken: string | null,
    username: string | null,
    token: string,
    createdAt: number,
    refreshToken?: string,
    expiresIn?: number,
    emailCode?: string
  ): string => {
    if (!host || !org || !scheme) {
      throw new ServiceError(
        invalidParametersError.title,
        invalidParametersError.message
      );
    }

    const parameters = new URLSearchParams({
      host,
      org,
      token,
      createdAt: String(createdAt)
    });
    if (refreshToken) parameters.set('refreshToken', refreshToken);
    if (expiresIn) parameters.set('expiresIn', String(expiresIn));
    if (codeVerifier) parameters.set('codeVerifier', codeVerifier);
    if (repo) parameters.set('repo', repo);
    if (service === 'gitlab' && !userToken && !emailCode && codeVerifier)
      parameters.append('codeVerifier', codeVerifier);
    if (service === 'bitbucket' && username)
      parameters.set('username', username);

    const callbackUrl = parseWebCallback(scheme);
    if (callbackUrl) {
      parameters.forEach((value, key) =>
        callbackUrl.searchParams.set(key, value)
      );

      return callbackUrl.toString();
    }

    return `${scheme}/authorization?${parameters.toString()}`;
  };

  const getSignInLink = (
    userToken: boolean = false,
    service: Service | undefined,
    org: string | undefined,
    ide: string | undefined,
    scheme: string | undefined,
    repo: string | undefined,
    userMachine: string | undefined
  ): string => {
    if (!service || !org || !ide || !scheme) {
      throw new ServiceError(
        invalidParametersError.title,
        invalidParametersError.message
      );
    }
    const parameters = [
      `host=${encodeURIComponent(service.host)}`,
      `service=${service.id}`,
      `org=${encodeURIComponent(org)}`,
      `scheme=${encodeURIComponent(scheme)}`,
      `ide=${encodeURIComponent(ide)}`
    ];
    if (repo) parameters.push(`repo=${encodeURIComponent(repo)}`);
    if (userMachine)
      parameters.push(`machine=${encodeURIComponent(userMachine)}`);
    if (userToken) parameters.push(`use_token=true`);

    return `/signin?${parameters.join('&')}`;
  };

  const sendInviteToTeammate = async (email: string) => {
    const invitedUser = teammates.find((it) => it.email === email);
    if (
      !invitedUser ||
      !inviteToken ||
      !user ||
      !organization ||
      !service ||
      !teammates
    ) {
      // TODO: Need to catch this
      throw new ServiceError(inviteError.title, inviteError.message);
    }

    const functions = getFunctions(app);
    const sendInviteEmail = httpsCallable(functions, 'sendInviteEmail');

    const params: InviteTeamMemberParams = {
      token: inviteToken,
      inviter: {
        name: user.displayName,
        photoUrl: user.photoURL
      },
      invited: {
        photoUrl: invitedUser.photoURL,
        name: invitedUser.name,
        email: invitedUser.email
      },
      organization: {
        name: repository ? repository.displayName : organization.displayName,
        photoUrl: organization.photoURL,
        repoOrOrgName: repository ? service.repoText : service.orgText
      },
      teammates: teammates.map((it) => ({
        name: it.name,
        photoUrl: it.photoURL
      }))
    };
    await sendInviteEmail(params);

    // Save emails sent in session storage to avoid being able to send multiple invites if page reloads
    const sentEmailsString = get(sessionEmailsSentKey);
    const sentEmails: string[] = sentEmailsString
      ? JSON.parse(sentEmailsString)
      : [];
    const updatedSentEmails = [...sentEmails, invitedUser.id];
    set(sessionEmailsSentKey, JSON.stringify(updatedSentEmails));
    setTeammates((c) =>
      c.map((it) => ({ ...it, sent: updatedSentEmails.includes(it.id) }))
    );
  };

  if (emailSent) {
    return (
      <section className={styles.container}>
        <EmailSentCard service={service} email={emailSent} />
      </section>
    );
  }

  return (
    <section className={styles.container}>
      <Helmet>
        <title>{APP_TITLE} - Signing in</title>
      </Helmet>

      {error && (
        <ErrorCard
          title={error.title}
          message={error.message}
          service={service}
          userToken={!isOauthSignin}
          getSignInLink={(userToken) =>
            getSignInLink(
              userToken,
              service,
              org,
              ide,
              scheme,
              repo,
              userMachine
            )
          }
        />
      )}

      {!error && service && (
        <CompleteCard
          loading={loading}
          tokenInput={false}
          service={service}
          user={user}
          organization={organization}
          repository={repository}
          teammates={teammates}
          ide={ide}
          scheme={scheme}
          token={token}
          deepLink={deepLink}
          onInviteClicked={sendInviteToTeammate}
        />
      )}
    </section>
  );
}
